export { LOCATIONS_SCOPE } from "./navigation.ts";

import type { MerchantLocation } from "@tdelivr/api-client/generated/commerce/merchant-location.js";
import type { StateClassKey } from "@tdelivr/design-tokens";

import { type BoardRow } from "./board.ts";

/**
 * What the branches board does to the locations MER-02 returns, away from any rendering.
 *
 * Built to `22 WP21 Merchant Portal.dc.html`, page `hours`:
 *
 * ```
 * hours:['Branches and hours','COM-01 · GEO','Where you trade from, when you are open, and how busy you are.',
 *   [['Branches','2','done',''],['Open now','1','done','Kilimani'],
 *    ['Busy mode','1','caution','+10 min prep'],['Holiday closures','1','waiting','20 Oct']],
 *   ['Branch','Hours today','Prep time'],
 *   [['Kilimani','07:00–22:00','20–30 min','caution','busy mode'],
 *    ['Westlands','closed today','—','closed','closed'],
 *    ['Mashujaa Day','20 Oct · all branches','—','waiting','scheduled closure']],
 *   'info','Busy mode adds preparation time to quotes so couriers don\\'t wait.']
 * ```
 *
 * ## The recorded blocker was half wrong, and the half that was wrong is the interesting half
 *
 * It read: _"nothing publishes a branch's opening hours or its usual preparation time"_, and added
 * that this is "a field absence and not a route absence". **The preparation time has been on
 * `merchant_locations` since that table's first migration**, as `preparation_minutes` with a
 * `CHECK (BETWEEN 0 AND 1440)`. What was true is the other half — nothing holds opening hours — and
 * what was also true, and not recorded, is that **MER-02 had no listing at all**: every operation
 * was addressed by `locationId`, so a screen could read, update or close a branch it already had an
 * identifier for and could not discover one.
 *
 * So this was a route absence *and* a field absence, and the note had it the other way round.
 * [ADR 0087](../../../docs/adr/0087-a-branch-nobody-could-list.md) publishes the listing.
 *
 * ## One of three columns is whole, one is partial, one has no source
 *
 * | column         | source                                                                   |
 * | -------------- | ------------------------------------------------------------------------ |
 * | `Branch`       | `name` — `Kilimani`, `Westlands`. A real name, like the products board's |
 * | `Hours today`  | **nothing.** No service on this platform holds an opening hour           |
 * | `Prep time`    | `preparationMinutes`, as one figure where the design draws a range       |
 *
 * **`Prep time` is the one worth reading carefully.** The design writes `20–30 min`, a range, and
 * the column holds one integer. A range would need a second field — a busy-mode increment, which is
 * what the design's `Busy mode · +10 min prep` tile is about — and nothing models one. So the cell
 * draws `20 min` and says that the published figure is a single usual time rather than a range,
 * which is honest about what a courier is being told.
 *
 * ## The design's third row is not a branch, and is not drawn
 *
 * `Mashujaa Day · 20 Oct · all branches · waiting · scheduled closure` is a **holiday**, and a
 * holiday is not a location: it has no code, no address, no coordinates and no preparation time.
 * Drawing it would mean inventing a row type, which is the same shape as the stock board's
 * `not reported` row — a fact about a different record, drawn in a grid of this one. The board says
 * once under the grid that a scheduled closure is not something this platform records.
 *
 * ## Two of the design's three badge words have no source
 *
 * `status` is `active` or `inactive`, which gives `open` and `closed`. The design's
 * `caution · busy mode` needs a busy mode nothing models, and its `waiting · scheduled closure`
 * needs the holiday above. So two words, and the reason for each absent one on the board.
 */

/** The most rows one request answers, which is this listing's own maximum. */
export const PER_FETCH = 200;

/**
 * The state class and the word, from the one thing a location's own status decides.
 *
 * `open` and `closed` rather than the service's `active` and `inactive`, because the column beside
 * them is `Hours today` and a merchant reading a row about trading hours reads "closed". The
 * published value is on the badge's title either way.
 */
export function stateOf(location: MerchantLocation): {
  classKey: StateClassKey;
  label: string;
  title: string;
} {
  return location.status === "active"
    ? {
        classKey: "done",
        label: "open",
        title:
          "MER-02 publishes active. Whether this branch is open right now is a different question and nothing on this platform holds an opening hour, so this is the branch's status rather than today's.",
      }
    : {
        classKey: "closed",
        label: "closed",
        title:
          location.statusReason === undefined
            ? "MER-02 publishes inactive and no reason was recorded when it was closed."
            : `MER-02 publishes inactive. The reason recorded when it was closed: ${location.statusReason}`,
      };
}

/** The states present in what came back, in board order, as the design's own `gridUI` does. */
export function statesPresent(locations: readonly MerchantLocation[]): readonly string[] {
  return [...new Set(locations.map((location) => stateOf(location).label))];
}

/**
 * The design's `Prep time` cell: one figure, not a range.
 *
 * `20 min`, where the design writes `20–30 min`. The second number is a busy-mode increment the
 * platform does not model, and a range invented from one figure would be a promise to a courier
 * about how long they will wait.
 */
export function prepTimeCell(location: MerchantLocation): string {
  return `${String(location.preparationMinutes)} min`;
}

/** One row of the branches board. */
export function branchRow(location: MerchantLocation): BoardRow {
  return {
    id: location.id,
    cells: [
      { text: location.name },
      {
        /*
         * The design's `07:00–22:00` and `closed today`. No service on this platform holds an
         * opening hour — swept across every contract — so this is the one column here with no
         * source at all. The branch's **status** is on the badge, which is a different statement
         * from whether it is open at this minute.
         */
        text: "",
        absent: true,
        title:
          "Nothing on this platform publishes a branch's opening hours. MER-02 holds a status and a time zone; the badge shows the status, which is not the same as whether the branch is open right now.",
      },
      {
        text: prepTimeCell(location),
        title: `${String(location.preparationMinutes)} minutes, which MER-02 publishes as one usual preparation time. The design draws a range; the second figure would be a busy-mode increment, and nothing on this platform models one.`,
      },
    ],
    state: stateOf(location),
    detail: [
      { label: "Branch", value: location.branchCode, mono: true },
      { label: "Address", value: location.addressLabel },
      { label: "Time zone", value: location.timeZone },
      { label: "Fulfilment", value: location.fulfillmentModes.join(", ") },
      { label: "Usual prep time", value: prepTimeCell(location) },
      ...(location.statusReason === undefined
        ? []
        : [{ label: "Closed because", value: location.statusReason }]),
    ],
    history: [
      {
        label: "Coordinates",
        value: `${location.coordinates.latitude.toFixed(5)}, ${location.coordinates.longitude.toFixed(5)}`,
        mono: true,
      },
      { label: "Version", value: `v${String(location.version)}` },
    ],
    glyph: "storefront",
  };
}

/**
 * Why the designed command cannot be sent.
 *
 * The design's form is `Branch (select) · Days (chips) · Opens (time) · Closes (time) · Usual prep
 * time (min)`. **Four of its five controls have nowhere to be stored**: MER-02 holds no days, no
 * opening time and no closing time, and this portal asks for no write scope on any board. The fifth
 * — the preparation time — is a real field and is the one `PATCH /v1/merchant-locations/{id}`
 * accepts, so a merchant who could write would be able to change that and nothing else on this
 * form.
 */
export const HOURS_REFUSAL =
  "This portal holds no write scope, and four of the five fields on Set branch hours have nowhere to be stored: MER-02 holds a status, a time zone and a usual preparation time, and no opening hours at all. The preparation time is the one field its update accepts.";

/**
 * The closing note, kept word for word, which on this portal is the usual case.
 *
 * `Busy mode adds preparation time to quotes so couriers don't wait.` It describes something the
 * platform would do if busy mode existed, and it is **not** a claim about a control on this screen
 * — so unlike the staff board's note it is not promising an enforcement that is absent. What is
 * absent is said separately, under the grid, where a reader is looking at the empty column.
 */
export const BOARD_NOTE = "Busy mode adds preparation time to quotes so couriers don't wait.";

/**
 * The sentence under the grid, which carries the three absences a reader is looking at.
 *
 * One sentence rather than three tooltips, because a reader seeing an empty column and two missing
 * badge words needs one explanation rather than a hunt.
 */
export const LISTING_NOTE =
  "Every branch recorded for your shop, in branch-code order. Opening hours are not held anywhere on this platform, so the Hours today column is empty and the badge shows the branch's status instead. Busy mode and a scheduled closure — the design's other two states, and its Mashujaa Day row — are not recorded either: a holiday is not a branch.";
